import { create } from 'zustand';
import { generateLoanRequests } from './loanGenerator';
import { rollEvents, getEffectiveEconomyState } from './economyEngine';
import { simulateMonth, calculatePortfolioRisk, checkBankHealth, calculateReputation } from './simulation';
import { checkAchievements } from './achievements';

const DIFFICULTY_CONFIG = {
  easy: { startingCapital: 10000000, label: 'Easy', emoji: '🟢' },
  medium: { startingCapital: 5000000, label: 'Medium', emoji: '🟡' },
  hard: { startingCapital: 2000000, label: 'Hard', emoji: '🔴' },
};

const INITIAL_STATE = {
  // Meta
  screen: 'setup', // setup | reviewing | simulating | gameover
  difficulty: 'medium',
  phase: 'reviewing', // reviewing | results

  // Bank
  initialCapital: 5000000,
  totalCapital: 5000000,
  liquidity: 5000000,
  totalLoansOutstanding: 0,
  bankHealth: 'healthy',
  reputation: 50,

  // Time
  month: 0,

  // Loans
  pendingLoans: [],       // Current month's loan requests
  activeLoans: [],        // Approved and active
  completedLoansLog: [],  // History of completed
  defaultsLog: [],        // History of defaults

  // Monthly tracking
  currentMonthIncome: 0,
  currentMonthDefaults: 0,
  currentMonthLosses: 0,
  monthlyHistory: [],

  // Economy
  economyState: 'normal',
  activeEvents: [],
  eventLog: [],
  pendingEventNotifications: [],

  // Risk
  riskScore: 0,

  // Stats for achievements
  totalLoansApproved: 0,
  totalLoansRejected: 0,
  totalProfit: 0,
  consecutiveNoDefaults: 0,
  blackSwansSurvived: 0,
  peakRiskScore: 0,
  allRejectedMonth: false,
  allApprovedMonth: false,
  startupLoansCompleted: 0,
  lowestRateApproved: Infinity,
  highestRateApproved: 0,

  // Achievements
  unlockedAchievements: new Set(),
  newAchievements: [],

  // Results from last simulation
  lastMonthResults: null,

  // UI
  showEventModal: false,
  currentEventModal: null,
  showAchievementToast: null,
  flashEffect: null,
};

const useGameStore = create((set, get) => ({
  ...INITIAL_STATE,

  // ─── SETUP ───
  setDifficulty: (d) => {
    const config = DIFFICULTY_CONFIG[d];
    set({
      difficulty: d,
      initialCapital: config.startingCapital,
      totalCapital: config.startingCapital,
      liquidity: config.startingCapital,
    });
  },

  startGame: () => {
    const state = get();
    const config = DIFFICULTY_CONFIG[state.difficulty];
    const cap = config.startingCapital;

    // Generate first batch of loans
    const loans = generateLoanRequests(1, 'normal', state.difficulty, 50);

    set({
      ...INITIAL_STATE,
      screen: 'reviewing',
      phase: 'reviewing',
      difficulty: state.difficulty,
      initialCapital: cap,
      totalCapital: cap,
      liquidity: cap,
      month: 1,
      pendingLoans: loans,
      unlockedAchievements: new Set(),
    });
  },

  // ─── LOAN DECISIONS ───
  approveLoan: (loanId, playerRate) => {
    const state = get();
    set({
      pendingLoans: state.pendingLoans.map(l =>
        l.id === loanId ? { ...l, approved: true, playerRate } : l
      ),
    });
  },

  rejectLoan: (loanId) => {
    const state = get();
    set({
      pendingLoans: state.pendingLoans.map(l =>
        l.id === loanId ? { ...l, approved: false } : l
      ),
    });
  },

  setLoanRate: (loanId, rate) => {
    const state = get();
    set({
      pendingLoans: state.pendingLoans.map(l =>
        l.id === loanId ? { ...l, playerRate: rate } : l
      ),
    });
  },

  // Check if all loans have been decided
  allDecided: () => {
    return get().pendingLoans.every(l => l.approved !== null);
  },

  // ─── ADVANCE MONTH (SIMULATE) ───
  advanceMonth: () => {
    const state = get();

    // 1. Process loan decisions
    const approved = state.pendingLoans.filter(l => l.approved === true);
    const rejected = state.pendingLoans.filter(l => l.approved === false);

    // Check if we can fund approved loans
    let availableCash = state.liquidity;
    const fundedLoans = [];

    for (const loan of approved) {
      if (loan.amount <= availableCash) {
        availableCash -= loan.amount;
        fundedLoans.push({
          ...loan,
          startMonth: state.month,
          remainingPrincipal: loan.amount,
          originalAmount: loan.amount,
          monthsRemaining: loan.duration,
          totalRepaid: 0,
        });
      }
    }

    // 2. Combine with existing active loans
    const allActiveLoans = [...state.activeLoans, ...fundedLoans];

    // 3. Roll for events
    const activeEventIds = new Set(state.activeEvents.map(e => e.id));
    const newEvents = rollEvents(state.difficulty, activeEventIds);

    // Process capital hits/boosts from new events
    let capitalChange = 0;
    for (const event of newEvents) {
      if (event.effects.capitalHit) capitalChange -= state.totalCapital * event.effects.capitalHit;
      if (event.effects.capitalBoost) capitalChange += state.totalCapital * event.effects.capitalBoost;
    }

    // Tick down existing events
    const updatedActiveEvents = [
      ...state.activeEvents
        .map(e => ({ ...e, remainingMonths: e.remainingMonths - 1 }))
        .filter(e => e.remainingMonths > 0),
      ...newEvents,
    ];

    // 4. Simulate repayments and defaults
    const simResults = simulateMonth(allActiveLoans, updatedActiveEvents, state.month, state.totalCapital);

    // 5. Update bank financials
    const newLiquidity = availableCash + simResults.totalRepayments + capitalChange;
    const totalLoansOutstanding = simResults.updatedLoans.reduce((s, l) => s + l.remainingPrincipal, 0);
    const newCapital = newLiquidity + totalLoansOutstanding;

    const profit = simResults.totalInterestIncome - simResults.defaultLosses;
    const totalProfit = state.totalProfit + profit;

    // 6. Risk and health
    const riskScore = calculatePortfolioRisk(simResults.updatedLoans, newCapital);

    const newMonthlyEntry = {
      month: state.month,
      income: simResults.totalInterestIncome,
      losses: simResults.defaultLosses,
      profit,
      defaults: simResults.defaults.length,
      activeLoans: simResults.updatedLoans.length,
      capital: newCapital,
      liquidity: newLiquidity,
      riskScore,
      loansApproved: fundedLoans.length,
      loansRejected: rejected.length,
    };

    const monthlyHistory = [...state.monthlyHistory, newMonthlyEntry];
    const reputation = calculateReputation(monthlyHistory);
    const bankHealth = checkBankHealth(newLiquidity, newCapital, state.initialCapital);

    // 7. Track achievement stats
    const noDefaultsStreak = simResults.defaults.length === 0
      ? state.consecutiveNoDefaults + 1
      : 0;

    const blackSwans = newEvents.filter(e => e.type === 'black_swan').length;
    const completedStartups = simResults.completedLoans.filter(l => l.borrowerType === 'startup').length;

    let lowestRate = state.lowestRateApproved;
    let highestRate = state.highestRateApproved;
    for (const loan of fundedLoans) {
      if (loan.playerRate < lowestRate) lowestRate = loan.playerRate;
      if (loan.playerRate > highestRate) highestRate = loan.playerRate;
    }

    // Achievement check state
    const achieveState = {
      totalLoansApproved: state.totalLoansApproved + fundedLoans.length,
      totalCapital: newCapital,
      month: state.month,
      totalProfit,
      consecutiveNoDefaults: noDefaultsStreak,
      blackSwansSurvived: state.blackSwansSurvived + (blackSwans > 0 && bankHealth !== 'bankrupt' ? 1 : 0),
      peakRiskScore: Math.max(state.peakRiskScore, riskScore),
      allRejectedMonth: state.pendingLoans.length > 0 && fundedLoans.length === 0,
      allApprovedMonth: state.pendingLoans.length > 0 && rejected.length === 0,
      startupLoansCompleted: state.startupLoansCompleted + completedStartups,
      lowestRateApproved: lowestRate,
      highestRateApproved: highestRate,
    };

    const newAchievements = checkAchievements(achieveState, state.unlockedAchievements);
    const updatedUnlocked = new Set(state.unlockedAchievements);
    for (const a of newAchievements) updatedUnlocked.add(a.id);

    // Flash effect
    let flashEffect = null;
    if (simResults.defaults.length > 0) flashEffect = 'red';
    else if (profit > state.totalCapital * 0.01) flashEffect = 'green';

    // 8. Generate next month's loans
    const economyState = getEffectiveEconomyState(updatedActiveEvents);
    const nextLoans = generateLoanRequests(state.month + 1, economyState, state.difficulty, reputation);

    // 9. Check game over
    const isGameOver = bankHealth === 'bankrupt';

    set({
      // Bank
      totalCapital: newCapital,
      liquidity: newLiquidity,
      totalLoansOutstanding,
      bankHealth,
      reputation,

      // Loans
      activeLoans: simResults.updatedLoans,
      completedLoansLog: [...state.completedLoansLog, ...simResults.completedLoans],
      defaultsLog: [...state.defaultsLog, ...simResults.defaults],
      pendingLoans: isGameOver ? [] : nextLoans,

      // Month results
      currentMonthIncome: simResults.totalInterestIncome,
      currentMonthDefaults: simResults.defaults.length,
      currentMonthLosses: simResults.defaultLosses,
      lastMonthResults: {
        ...simResults,
        newEvents,
        profit,
        fundedLoans,
        rejected,
      },
      monthlyHistory,

      // Economy
      economyState,
      activeEvents: updatedActiveEvents,
      eventLog: [...state.eventLog, ...newEvents.map(e => ({ ...e, month: state.month }))],
      pendingEventNotifications: newEvents,
      showEventModal: newEvents.length > 0,
      currentEventModal: newEvents.length > 0 ? newEvents[0] : null,

      // Risk
      riskScore,

      // Stats
      totalLoansApproved: achieveState.totalLoansApproved,
      totalLoansRejected: state.totalLoansRejected + rejected.length,
      totalProfit,
      consecutiveNoDefaults: noDefaultsStreak,
      blackSwansSurvived: achieveState.blackSwansSurvived,
      peakRiskScore: achieveState.peakRiskScore,
      allRejectedMonth: achieveState.allRejectedMonth,
      allApprovedMonth: achieveState.allApprovedMonth,
      startupLoansCompleted: achieveState.startupLoansCompleted,
      lowestRateApproved: lowestRate,
      highestRateApproved: highestRate,

      // Achievements
      unlockedAchievements: updatedUnlocked,
      newAchievements,
      showAchievementToast: newAchievements.length > 0 ? newAchievements[0] : null,

      // Meta
      month: state.month + 1,
      phase: 'results',
      screen: isGameOver ? 'gameover' : 'reviewing',
      flashEffect,
    });

    // Clear flash
    setTimeout(() => set({ flashEffect: null }), 800);
  },

  continueToReview: () => {
    set({ phase: 'reviewing' });
  },

  // ─── EVENT MODAL ───
  dismissEvent: () => {
    const state = get();
    const remaining = state.pendingEventNotifications.slice(1);
    set({
      pendingEventNotifications: remaining,
      showEventModal: remaining.length > 0,
      currentEventModal: remaining.length > 0 ? remaining[0] : null,
    });
  },

  dismissAchievement: () => {
    const state = get();
    const remaining = state.newAchievements.slice(1);
    set({
      newAchievements: remaining,
      showAchievementToast: remaining.length > 0 ? remaining[0] : null,
    });
  },

  // ─── SAVE / LOAD ───
  saveGame: () => {
    const state = get();
    const saveData = { ...state };
    // Convert Set to Array for JSON
    saveData.unlockedAchievements = [...state.unlockedAchievements];
    delete saveData.showEventModal;
    delete saveData.currentEventModal;
    delete saveData.showAchievementToast;
    delete saveData.flashEffect;
    try {
      localStorage.setItem('bankEmpireSave', JSON.stringify(saveData));
      return true;
    } catch {
      return false;
    }
  },

  loadGame: () => {
    try {
      const raw = localStorage.getItem('bankEmpireSave');
      if (!raw) return false;
      const data = JSON.parse(raw);
      data.unlockedAchievements = new Set(data.unlockedAchievements || []);
      data.showEventModal = false;
      data.currentEventModal = null;
      data.showAchievementToast = null;
      data.flashEffect = null;
      data.pendingEventNotifications = [];
      data.newAchievements = [];
      set(data);
      return true;
    } catch {
      return false;
    }
  },

  hasSave: () => {
    return !!localStorage.getItem('bankEmpireSave');
  },

  // ─── RESET ───
  resetGame: () => set({ ...INITIAL_STATE, unlockedAchievements: new Set() }),
}));

export default useGameStore;
export { DIFFICULTY_CONFIG };
